import {
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import * as React from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";

import { useAuth } from "@/providers";
import { CopyLinkButton, RobotoTooltip } from "@/shared/components";
import { AlertDialog } from "@/shared/components/AlertDialog";
import { CreateEntityButton } from "@/shared/components/CreateEntityButton";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  ResourceView,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  type ResourceViewEmptyStateContent,
} from "@/shared/components/ResourceView";
import {
  ROW_ACTIONS_COLUMN_ID,
  RowActionsMenu,
  rowActionsColumn,
  type RowAction,
} from "@/shared/components/RowActionsMenu";
import { StatusBadge, STATUS_BADGES } from "@/shared/components/StatusBadge";
import {
  canEditSkill,
  consumeSkillDraft,
  SkillAccessibility,
  SkillDraft,
  SkillSummary,
} from "@/shared/domain/skills";
import {
  useDeleteSkill,
  useDeleteVersion,
  useSetSubscription,
  useSkills,
  useSkillVersions,
  useSubscribeSkill,
  useUnsubscribeSkill,
  useUpdateSkillMetadata,
} from "@/shared/domain/skills/hooks";
import {
  ENTITY_ICONS,
  Link as LinkIcon,
  MinusCircle,
  Plus,
  Trash2,
} from "@/shared/foundation/icons";
import {
  DeleteIcon,
  EditIcon,
  PushPinIcon,
  PushPinOutlinedIcon,
  VisibilityIcon,
} from "@/shared/foundation/icons/mui";
import {
  RobotoButton,
  Toggle,
  ToggleGroup,
} from "@/shared/foundation/primitives";
import {
  ColumnVisibility,
  DataTable,
  colId,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation";

import { skillMatchesFilters } from "./filterSkills";
import { SkillEditor } from "./SkillEditor";
import {
  buildSkillShareLink,
  parseLegacySkillLink,
  skillEditPath,
  type SkillEditSeedState,
} from "./skillLinks";
import styles from "./Skills.module.css";
import {
  countSkillsByScope,
  skillsInScope,
  type SkillsScope,
} from "./skillScopes";
import { SkillTagsDialog } from "./SkillTagsDialog";
import { resolveUpdateHandoff, skillListStatus } from "./updateHandoff";
import { VisibilityChip } from "./VisibilityChip";
import { VisibilitySelect } from "./VisibilitySelect";

interface VersionDeleteTarget {
  summary: SkillSummary;
  version: number;
  isLastVersion: boolean;
}

/**
 * The Skills tab, plus the one redirect it owes to history.
 *
 * Links minted before skills had their own route arrive here as
 * `?skillId=…` (the agent template's old "Edit skill" link). Each one now names
 * a page, so send them there rather than reproducing the filter-and-expand
 * landing they used to get. Split from {@link SkillsList} so the redirect can
 * short-circuit before the list's own hooks — and its fetch — run at all.
 */
export function Skills() {
  const [searchParams] = useSearchParams();
  const legacySkillId = parseLegacySkillLink(searchParams);

  if (legacySkillId !== null) {
    // `replace`: the query-param URL is a waypoint, not a destination. Leaving
    // it in history would make Back bounce the user straight forward again.
    return <Navigate to={Goto.createSkillRoute(legacySkillId)} replace />;
  }
  return <SkillsList />;
}

/**
 * Columns shown on a first visit; the "…" row menu is always on. The AI
 * column is dropped from the Organization scope's column array, so listing
 * it here is harmless there.
 */
const DEFAULT_VISIBLE_COLUMN_IDS = [
  "name",
  "created_by",
  "visibility",
  "tags",
  "latest",
  "ai_indicator",
];

/** The scope toggle's segments, in display order. */
const SCOPE_OPTIONS: { value: SkillsScope; label: string }[] = [
  { value: "all", label: "All" },
  { value: "personal", label: "My skills" },
  { value: "org", label: "Organization" },
];

function formatSkillCount(count: number): string {
  return count === 1 ? "1 skill" : `${count} skills`;
}

/**
 * The empty table's "nothing here" copy when no filter is the cause: an org
 * with no skills at all, or a scope with none while other scopes have some.
 */
function skillsNoDataContent({
  hasAnySkill,
  scope,
  onCreate,
  onShowAll,
}: {
  hasAnySkill: boolean;
  scope: SkillsScope;
  onCreate: () => void;
  onShowAll: () => void;
}): ResourceViewEmptyStateContent {
  if (!hasAnySkill || scope === "all") {
    return {
      icon: ENTITY_ICONS.skill,
      title: "No skills yet",
      description:
        "Teach the assistant a repeatable task once, then reuse it in any chat.",
      action: (
        <CreateEntityButton
          entity="skill"
          variant="secondary"
          onClick={onCreate}
        />
      ),
    };
  }
  const label = SCOPE_OPTIONS.find((option) => option.value === scope)?.label;
  return {
    icon: ENTITY_ICONS.skill,
    title: `No skills in ${label}`,
    description:
      scope === "personal"
        ? "Skills you create or subscribe to show up here."
        : "Skills shared with the organization show up here.",
    action: (
      <RobotoButton variant="secondary" size="sm" onClick={onShowAll}>
        Show all skills
      </RobotoButton>
    ),
  };
}

function SkillsList() {
  const { currentOrganization, currentUser } = useAuth();
  const orgId = currentOrganization?.org_id;
  const userId = currentUser?.user_id ?? "";

  // One unscoped load; the toggle narrows it in memory so every scope's count
  // is known without a request per scope.
  const [scope, setScope] = React.useState<SkillsScope>("all");
  const skillsQuery = useSkills(orgId);
  const deleteSkill = useDeleteSkill(orgId);
  const updateMetadata = useUpdateSkillMetadata(orgId);
  const subscribeSkill = useSubscribeSkill(orgId);
  const unsubscribeSkill = useUnsubscribeSkill(orgId);
  const setSubscription = useSetSubscription(orgId);

  // Pending create-skill draft handed over from the chat tool's
  // `client_create_skill` approval, which stashes a draft and navigates here.
  // When present we open the create editor seeded with it. Cleared by a manual
  // "New skill" click so that path always starts from the empty form.
  const [createSeed, setCreateSeed] = React.useState<SkillDraft | null>(null);
  // An update draft awaiting resolution against the loaded skill list. The
  // draft is consumed on navigation (before skills have necessarily loaded), so
  // we park it here and a separate effect resolves the target once `allSkills`
  // is available. Cleared once resolved (opened for edit) or rejected (error).
  const [pendingUpdateDraft, setPendingUpdateDraft] =
    React.useState<SkillDraft | null>(null);
  // Set when a chat-tool update draft names a skill the caller can't edit (no
  // visible skill by that name, or one they lack edit rights on). Surfaced as a
  // dismissible alert instead of silently creating a new skill. Authoritative
  // edit-permission enforcement is server-side at save; the browser resolves
  // what it can from the loaded list (name + client-side `canEditSkill`).
  const [updateError, setUpdateError] = React.useState<string | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);

  // Consume the draft on mount AND on every subsequent navigation to this
  // route, keyed on `location.key`. The chat tool navigates here after
  // stashing, but when the Skills page is already mounted (e.g. the chat panel
  // is open over it) that navigation re-keys the location without remounting —
  // a mount-only read would miss the draft and the editor would never open.
  // `consumeSkillDraft` is StrictMode-safe and clears the slot, so a navigation
  // with no pending draft (a filter change, a normal visit) reads null and is a
  // no-op.
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const draft = consumeSkillDraft();
    if (draft === null) {
      return;
    }
    setUpdateError(null);
    if (draft.targetSkillName !== undefined) {
      // Update handoff: the target must be resolved against the loaded skill
      // list, which may not be ready yet. Park the draft for the resolution
      // effect. It resolves against every loaded skill, whichever scope the
      // toggle shows; `resolveUpdateHandoff` rejects skills the caller can't
      // edit.
      setPendingUpdateDraft(draft);
      return;
    }
    setCreateSeed(draft);
    // A create draft supersedes an update draft still waiting on the list;
    // leaving it parked would let it reopen the editor over this one.
    setPendingUpdateDraft(null);
    setEditorOpen(true);
  }, [location.key]);
  // Track which skill rows have an in-flight subscription mutation so the
  // toggle / button on that row spins individually instead of disabling the
  // whole tab.
  const [pendingSubscriptionIds, setPendingSubscriptionIds] = React.useState<
    Set<string>
  >(() => new Set());
  const [pendingAiVersionIds, setPendingAiVersionIds] = React.useState<
    Set<string>
  >(() => new Set());
  // Tags-only modal opened from the inline double-click affordance on the
  // Tags column. Separate from the metadata editor so the user gets exactly
  // the field they clicked into — nothing else.
  const [tagsEditTarget, setTagsEditTarget] =
    React.useState<SkillSummary | null>(null);
  // Track which skill_id (if any) is currently being deleted so we only
  // disable that row's button. Sharing a single mutation across all rows
  // would otherwise disable every row whenever any one delete is in flight.
  const [pendingDeleteSkillId, setPendingDeleteSkillId] = React.useState<
    string | null
  >(null);
  const [pendingDeleteSkill, setPendingDeleteSkill] =
    React.useState<SkillSummary | null>(null);
  const [pendingDeleteVersion, setPendingDeleteVersion] =
    React.useState<VersionDeleteTarget | null>(null);
  // Tracks rows whose visibility toggle is in flight. Set (not single-string)
  // because there's no dialog gating clicks across rows — two concurrent
  // toggles on different chips should each show their own spinner.
  const [pendingVisibilityIds, setPendingVisibilityIds] = React.useState<
    Set<string>
  >(() => new Set());

  const view = useResourceView<SkillSummary>({
    getRowId: (row) => row.skill.skill_id,
    initialSort: { columnId: "name", direction: "asc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });
  const showNotification = useNotification();
  const copyLink = React.useCallback(
    (url: string) => {
      navigator.clipboard
        .writeText(url)
        .then(() => showNotification("Link copied"))
        .catch(() => showNotification("Couldn't copy the link", "error"));
    },
    [showNotification],
  );

  function openCreate() {
    // Manual create starts from a blank form — drop any chat-tool draft seed,
    // and any update draft still awaiting the skill list: the user has chosen
    // what to do, so a late resolution must not navigate away from the form.
    setCreateSeed(null);
    setPendingUpdateDraft(null);
    setEditorOpen(true);
  }

  async function performSkillDelete(): Promise<void> {
    if (pendingDeleteSkill === null) {
      return;
    }
    setPendingDeleteSkillId(pendingDeleteSkill.skill.skill_id);
    try {
      await deleteSkill.mutateAsync(pendingDeleteSkill.skill.skill_id);
    } finally {
      setPendingDeleteSkillId(null);
      setPendingDeleteSkill(null);
    }
  }

  const setPending = React.useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<Set<string>>>,
      skillId: string,
      pending: boolean,
    ) => {
      setter((prev) => {
        const next = new Set(prev);
        if (pending) {
          next.add(skillId);
        } else {
          next.delete(skillId);
        }
        return next;
      });
    },
    [],
  );

  const handleSubscribe = React.useCallback(
    async (summary: SkillSummary) => {
      const skillId = summary.skill.skill_id;
      setPending(setPendingSubscriptionIds, skillId, true);
      try {
        await subscribeSkill.mutateAsync({ skillId });
      } finally {
        setPending(setPendingSubscriptionIds, skillId, false);
      }
    },
    [setPending, subscribeSkill],
  );

  const handleUnsubscribe = React.useCallback(
    async (summary: SkillSummary) => {
      const skillId = summary.skill.skill_id;
      setPending(setPendingSubscriptionIds, skillId, true);
      try {
        await unsubscribeSkill.mutateAsync({ skillId });
      } finally {
        setPending(setPendingSubscriptionIds, skillId, false);
      }
    },
    [setPending, unsubscribeSkill],
  );

  const handleSetAiVersion = React.useCallback(
    async (skillId: string, version: number | null) => {
      setPending(setPendingAiVersionIds, skillId, true);
      try {
        await setSubscription.mutateAsync({
          skillId,
          request: { ai_version: version },
        });
      } finally {
        setPending(setPendingAiVersionIds, skillId, false);
      }
    },
    [setPending, setSubscription],
  );

  const changeVisibility = React.useCallback(
    async (summary: SkillSummary, next: SkillAccessibility) => {
      if (summary.skill.created_by !== userId) {
        return;
      }
      // Selecting the tier the skill is already on is a no-op.
      if (next === summary.skill.accessibility) {
        return;
      }
      const skillId = summary.skill.skill_id;
      setPendingVisibilityIds((prev) => {
        const updated = new Set(prev);
        updated.add(skillId);
        return updated;
      });
      try {
        await updateMetadata.mutateAsync({
          skillId,
          request: { accessibility: next },
        });
      } finally {
        setPendingVisibilityIds((prev) => {
          if (!prev.has(skillId)) {
            return prev;
          }
          const updated = new Set(prev);
          updated.delete(skillId);
          return updated;
        });
      }
    },
    [updateMetadata, userId],
  );

  const allSkills = React.useMemo(
    () => skillsQuery.data?.items ?? [],
    [skillsQuery.data],
  );
  const scopedSkills = React.useMemo(
    () => skillsInScope(allSkills, scope, userId),
    [allSkills, scope, userId],
  );
  const scopeCounts = React.useMemo(
    () => countSkillsByScope(allSkills, userId),
    [allSkills, userId],
  );

  // Derived rather than read inside the effect so the dependency is a stable
  // primitive: the effect re-runs when the list's answerability changes, not
  // on every new query object identity.
  const listStatus = skillListStatus(skillsQuery);

  // Resolve a parked chat-tool update draft against the loaded skills, then
  // send the user to the target skill's page in edit mode with the AI's
  // content riding along as router state for the edit form to overlay on the
  // loaded version. The decision itself lives in
  // `resolveUpdateHandoff`; this effect only applies it.
  //
  // Anything the user does in the meantime — opening the create editor,
  // accepting another draft — clears the parked draft at the point of that
  // action, so this can't fire late and navigate away from whatever they chose
  // instead.
  React.useEffect(() => {
    if (pendingUpdateDraft === null || orgId === undefined) {
      return;
    }
    const resolution = resolveUpdateHandoff({
      draft: pendingUpdateDraft,
      skills: allSkills,
      userId,
      listStatus,
    });
    if (resolution.kind === "wait") {
      return;
    }
    if (resolution.kind === "open") {
      const state: SkillEditSeedState = { skillContentSeed: resolution.seed };
      void navigate(
        skillEditPath(resolution.summary.skill.skill_id, resolution.version),
        { state },
      );
    } else {
      setUpdateError(resolution.message);
    }
    setPendingUpdateDraft(null);
  }, [pendingUpdateDraft, allSkills, listStatus, navigate, orgId, userId]);

  // Skills filter in memory rather than server-side, so we work with the
  // structured `Filter[]` directly; `"none"` and `"roboql"` (never
  // produced here in v1) collapse to an empty list with the seed match mode.
  // Memoize the narrowed slice so dependent hooks stay stable when filter
  // state hasn't moved — the ternary creates a fresh `[]` per render otherwise.
  const structuredFilters = React.useMemo<Filter[]>(
    () => (view.filters.type === "structured" ? view.filters.filters : []),
    [view.filters],
  );
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";
  const { resetCurrentPage } = view.table;

  // Field definitions for the shared DataTable filter widget. The tag field
  // is a ``set`` (CONTAINS / NOT_CONTAINS membership) whose options are the
  // distinct tags actually in use in the selected scope, so it is recomputed
  // when the scope changes. Name / Author are restricted to the two
  // comparators that carry meaning for these free-text columns.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(() => {
    const allTags = new Set<string>();
    for (const summary of scopedSkills) {
      for (const tag of summary.skill.tags) {
        allTags.add(tag);
      }
    }
    return [
      {
        type: "string",
        field: "name",
        name: "Name",
        allowedComparators: ["CONTAINS", "EQUALS"],
      },
      {
        type: "string",
        field: "created_by",
        name: "Author",
        allowedComparators: ["CONTAINS", "EQUALS"],
      },
      {
        type: "set",
        field: "tags",
        name: "Tag",
        options: [...allTags].sort((a, b) => a.localeCompare(b)),
      },
    ];
  }, [scopedSkills]);

  // Clicking a tag chip in a row appends an exact-match tag filter — the
  // same affordance the handwritten chip strip used to provide, now routed
  // through the DataTable filter state. Deduped so a repeated click on an
  // already-applied tag is a no-op rather than stacking identical chips.
  const addTagFilter = React.useCallback(
    (tag: string) => {
      const alreadyApplied = structuredFilters.some(
        (f) =>
          f.type === "set" &&
          f.field === "tags" &&
          f.comparator === "CONTAINS" &&
          f.values.length === 1 &&
          f.values[0].value === tag,
      );
      if (alreadyApplied) {
        return;
      }
      view.commit({
        type: "structured",
        filter: {
          type: "set",
          field: "tags",
          comparator: "CONTAINS",
          values: [{ value: tag, label: tag }],
        },
      });
    },
    [structuredFilters, view],
  );

  // Skills are loaded in full, so the DataTable filter widget's `Filter[]`
  // is evaluated in memory rather than sent server-side as a condition.
  const filteredSkills = React.useMemo(() => {
    if (structuredFilters.length === 0) {
      return scopedSkills;
    }
    return scopedSkills.filter((s) =>
      skillMatchesFilters(s, structuredFilters, matchMode),
    );
  }, [scopedSkills, structuredFilters, matchMode]);

  // The DataTable primitive is wired for server-side sort (its DEFAULT_COL_DEF
  // sets `comparator: () => 0` to inhibit AG Grid's client-side reorder so the
  // server stays authoritative). Skills load in one shot, so we apply the sort
  // ourselves before slicing — column headers still surface the affordance and
  // direction indicator via `sortable: true` on the columns below.
  const sortedSkills = React.useMemo(() => {
    if (view.table.sort === null) {
      return filteredSkills;
    }
    const { columnId, direction } = view.table.sort;
    const direction_factor = direction === "asc" ? 1 : -1;
    const key = (s: SkillSummary): string => {
      switch (columnId) {
        case "name":
          return s.skill.name;
        case "created_by":
          return s.skill.created_by;
        default:
          return "";
      }
    };
    return [...filteredSkills].sort(
      (a, b) =>
        key(a).localeCompare(key(b), undefined, { sensitivity: "base" }) *
        direction_factor,
    );
  }, [filteredSkills, view.table.sort]);

  // Client-side pagination: the skills list is fetched in one shot, but the
  // DataTable footer drives its own page state. Slice locally so prev/next
  // and the "1-N of M" range label behave.
  const start = view.table.currentPage * view.table.pageSize;
  const end = start + view.table.pageSize;
  const visibleSkills = React.useMemo(
    () => sortedSkills.slice(start, end),
    [sortedSkills, start, end],
  );
  const hasNextPage = end < sortedSkills.length;

  const columns = React.useMemo<ColDef<SkillSummary>[]>(() => {
    const baseColumns: ColDef<SkillSummary>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        // The value getter stays so sorting and the filter widget still see a
        // plain string; the renderer makes the name the way into the skill's
        // own page, which is where reading and editing it now happen.
        valueGetter: (p) => p.data?.skill.name ?? "",
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          return (
            <Link
              className={styles.nameLink}
              to={Goto.createSkillRoute(p.data.skill.skill_id)}
            >
              {p.data.skill.name}
            </Link>
          );
        },
      },
      {
        colId: "created_by",
        headerName: "Author",
        flex: 1,
        sortable: true,
        valueGetter: (p) => p.data?.skill.created_by ?? "",
      },
      {
        colId: "visibility",
        headerName: "Visibility",
        width: 160,
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const accessibility = summary.skill.accessibility;
          const isAuthor = summary.skill.created_by === userId;
          const isPending = pendingVisibilityIds.has(summary.skill.skill_id);
          // Non-authors can't change visibility — show a read-only chip.
          if (!isAuthor) {
            return (
              <RobotoTooltip title="Only the author can change visibility">
                <span>
                  <VisibilityChip accessibility={accessibility} />
                </span>
              </RobotoTooltip>
            );
          }
          // Author: a dropdown that jumps straight to any tier. Unlike a
          // click-to-cycle chip, this never forces a downgrade through
          // `private` (which would prune every non-author subscription).
          return (
            <VisibilitySelect
              accessibility={accessibility}
              isPending={isPending}
              onChange={(next) => void changeVisibility(summary, next)}
            />
          );
        },
      },
      {
        colId: "tags",
        headerName: "Tags",
        flex: 2,
        sortable: false,
        // AG Grid fires dblclick at the cell level regardless of inner DOM —
        // way more reliable than putting an onDoubleClick on a wrapper div
        // that gets sized to its content (a chip-tall band, or zero on empty
        // cells, both of which we've broken before). Chip-originated dblclicks
        // are filtered out here so a fast double-tap on a tag chip stays a
        // filter action and never escalates into the editor.
        onCellDoubleClicked: (event) => {
          const summary = event.data;
          if (!summary || !canEditSkill(summary, userId)) {
            return;
          }
          const target = (event.event?.target ?? null) as HTMLElement | null;
          if (target?.closest("button")) {
            return;
          }
          setTagsEditTarget(summary);
        },
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const tags = summary.skill.tags;
          const canEdit = canEditSkill(summary, userId);
          let cellTitle: string | undefined;
          if (canEdit && tags.length > 0) {
            cellTitle = "Click a tag to filter; double-click to edit";
          } else if (canEdit) {
            cellTitle = "Double-click to add tags";
          }
          return (
            <div className={styles.tagsCell} title={cellTitle}>
              <SkillTagsCell tags={tags} onTagClick={addTagFilter} />
            </div>
          );
        },
      },
      {
        colId: "latest",
        headerName: "Latest version",
        width: 140,
        valueGetter: (p) =>
          p.data?.latest_version
            ? `v${p.data.latest_version.version}`
            : "Empty",
      },
      // The AI-version chip only ever applies to rows the caller has subscribed
      // to. Most Organization rows are unsubscribed, so the column would be
      // mostly empty there — hide it in that scope.
      ...(scope === "org"
        ? []
        : [
            {
              colId: "ai_indicator",
              headerName: "AI",
              width: 90,
              cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
                if (!p.data) {
                  return null;
                }
                const pinned = p.data.subscription?.ai_version;
                if (pinned === null || pinned === undefined) {
                  return null;
                }
                const latest = p.data.latest_version?.version;
                const isStale = latest !== undefined && latest !== pinned;
                return (
                  <StatusBadge
                    status={
                      isStale
                        ? STATUS_BADGES.skillVersion.stale
                        : STATUS_BADGES.skillVersion.latest
                    }
                    label={`v${pinned}`}
                    tooltip={
                      isStale
                        ? `AI invokes v${pinned} (latest is v${latest}). Switch version in the expanded panel.`
                        : `AI invokes v${pinned} (latest).`
                    }
                    data-testid="skill-ai-version"
                  />
                );
              },
            } satisfies ColDef<SkillSummary>,
          ]),
      rowActionsColumn<SkillSummary>((summary) => {
        const isAuthor = summary.skill.created_by === userId;
        const subscribed = summary.subscription !== null;
        const subscriptionPending = pendingSubscriptionIds.has(
          summary.skill.skill_id,
        );
        // Links the skill, not a version: whoever opens it lands on this
        // skill's page showing whatever the latest version is by then, rather
        // than whatever it was when the link was copied.
        const actions: RowAction[] = [
          {
            label: "Copy link",
            icon: <LinkIcon />,
            onClick: () =>
              copyLink(
                buildSkillShareLink({ skillId: summary.skill.skill_id }),
              ),
          },
        ];
        if (!isAuthor && !subscribed) {
          // Teammate's skill, not subscribed: subscribe, no edit / delete.
          actions.push({
            label: "Subscribe",
            icon: <Plus />,
            disabled: subscriptionPending,
            onClick: () => void handleSubscribe(summary),
          });
        } else if (!isAuthor) {
          // Subscribed-but-not-authored: Unsubscribe removes it from My
          // skills. Editing lives on the skill's page, reached by its name.
          actions.push({
            label: "Unsubscribe",
            icon: <MinusCircle />,
            disabled: subscriptionPending,
            onClick: () => void handleUnsubscribe(summary),
          });
        } else {
          // Author: delete the whole skill, after a confirm.
          actions.push({
            label: "Delete skill",
            icon: <Trash2 />,
            danger: true,
            disabled: pendingDeleteSkillId === summary.skill.skill_id,
            onClick: () => setPendingDeleteSkill(summary),
          });
        }
        return <RowActionsMenu label={summary.skill.name} actions={actions} />;
      }),
    ];
    // DataTable's DEFAULT_COL_DEF hides columns by default (the
    // column-visibility picker is the source of truth for opt-in consumers).
    // Skills doesn't use that system — every column is meant to be visible —
    // so flip the default. Spread order lets any column still opt out by
    // setting `hide: true` explicitly.
    return baseColumns.map((c) => ({ hide: false, ...c }));
  }, [
    addTagFilter,
    copyLink,
    handleSubscribe,
    handleUnsubscribe,
    changeVisibility,
    pendingDeleteSkillId,
    pendingSubscriptionIds,
    pendingVisibilityIds,
    scope,
    userId,
  ]);

  const pickerOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns
        .filter((column) => colId(column) !== ROW_ACTIONS_COLUMN_ID)
        .map((column) => ({
          id: colId(column),
          label: column.headerName ?? colId(column),
        })),
    [columns],
  );

  const loading = deriveLoadingState({
    isLoading: orgId === undefined || skillsQuery.isLoading,
    isFetching: skillsQuery.isFetching,
  });

  const renderVersionsPanel = React.useCallback(
    (summary: SkillSummary) => (
      <div className={styles.expandedContainer}>
        <VersionsSubTable
          summary={summary}
          isAuthor={summary.skill.created_by === userId}
          canEdit={canEditSkill(summary, userId)}
          subscribed={summary.subscription !== null}
          aiPinnedVersion={summary.subscription?.ai_version ?? null}
          aiVersionPending={pendingAiVersionIds.has(summary.skill.skill_id)}
          onSetAiVersion={(version) =>
            void handleSetAiVersion(summary.skill.skill_id, version)
          }
          onRequestDeleteVersion={(version, isLast) =>
            setPendingDeleteVersion({
              summary,
              version,
              isLastVersion: isLast,
            })
          }
        />
      </div>
    ),
    [handleSetAiVersion, pendingAiVersionIds, userId],
  );

  return (
    <div className={styles.page}>
      {skillsQuery.isError && (
        <Alert severity="error" className={styles.errorAlert}>
          Failed to load skills.
        </Alert>
      )}

      {updateError !== null && (
        <Alert
          severity="error"
          className={styles.errorAlert}
          onClose={() => setUpdateError(null)}
        >
          {updateError}
        </Alert>
      )}

      <div className={styles.tableSlot}>
        <ResourceView.Root>
          <ResourceView.Actions>
            <ResourceView.Filters
              filterDefinitions={filterDefinitions}
              filters={view.filters}
              onFilterSelected={view.commit}
            />
            <ColumnVisibility
              options={pickerOptions}
              visibleColumnIds={view.table.visibleColumnIds}
              setColumnVisibility={view.table.setColumnVisibility}
            />
            <span className={styles.toolbarDivider} aria-hidden="true" />
            <ToggleGroup<SkillsScope>
              aria-label="Skills to show"
              value={[scope]}
              onValueChange={(next) => {
                // Clicking the selected scope again deselects it, reporting an
                // empty array; keep the current scope instead.
                if (next[0] !== undefined) {
                  setScope(next[0]);
                  resetCurrentPage();
                }
              }}
            >
              {SCOPE_OPTIONS.map(({ value, label }) => (
                <Toggle
                  key={value}
                  value={value}
                  aria-label={`${label}, ${formatSkillCount(scopeCounts[value])}`}
                >
                  {label}
                  <span className={styles.scopeCount}>
                    {scopeCounts[value]}
                  </span>
                </Toggle>
              ))}
            </ToggleGroup>
            <div className={styles.actionsRight}>
              <CreateEntityButton entity="skill" onClick={openCreate} />
            </div>
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={filterDefinitions}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<SkillSummary>
            table={view.table}
            hasNextPage={hasNextPage}
            loading={loading}
            columns={columns}
            data={visibleSkills}
            totalRowCount={filteredSkills.length}
            noRowsContent={
              <ResourceView.EmptyState
                // Broadest cause first: a scope with no skills is empty
                // whatever the filters say, so it explains itself before any
                // filter is blamed.
                hasActiveFilters={
                  structuredFilters.length > 0 && scopedSkills.length > 0
                }
                noData={skillsNoDataContent({
                  hasAnySkill: allSkills.length > 0,
                  scope,
                  onCreate: openCreate,
                  onShowAll: () => {
                    setScope("all");
                    resetCurrentPage();
                  },
                })}
                noMatches={{
                  icon: ENTITY_ICONS.skill,
                  title: "No matching skills",
                  description: "Try adjusting or clearing your filters.",
                }}
              />
            }
            expansion={{ renderRow: renderVersionsPanel }}
          />
        </ResourceView.Root>
      </div>

      <SkillEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initialValues={createSeed}
        orgId={orgId}
      />

      <SkillTagsDialog
        open={tagsEditTarget !== null}
        onClose={() => setTagsEditTarget(null)}
        summary={tagsEditTarget}
        orgId={orgId}
      />

      <AlertDialog
        dialogOpen={pendingDeleteSkill !== null}
        handleClose={() => {
          if (pendingDeleteSkillId === null) {
            setPendingDeleteSkill(null);
          }
        }}
        dialogTitle={
          pendingDeleteSkill
            ? `Delete skill "${pendingDeleteSkill.skill.name}"?`
            : "Delete skill"
        }
        dialogContent={
          <div className={styles.deleteWarning}>
            <span className={styles.deleteWarningEmphasis}>
              This is permanent.
            </span>
            <span>
              The skill and all of its versions will be removed. Existing chats
              keep any skill_invocation messages they&apos;ve already produced —
              the body is captured at invocation time. You can re-create a skill
              with the same name afterwards.
            </span>
          </div>
        }
        dialogAction={performSkillDelete}
      />

      <DeleteVersionDialog
        target={pendingDeleteVersion}
        orgId={orgId}
        onClose={() => setPendingDeleteVersion(null)}
      />
    </div>
  );
}

const MAX_VISIBLE_TAGS = 2;

interface SkillTagsCellProps {
  tags: string[];
  /** Optional click handler — when set, each tag becomes a filter trigger. */
  onTagClick?: (tag: string) => void;
}

function SkillTagsCell({ tags, onTagClick }: SkillTagsCellProps) {
  return (
    // Keep a tag click from bubbling to the cell: clicking a tag should only
    // ever add a filter, never open the editor.
    <span
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <TagGroup
        tags={tags}
        maxDisplayCount={MAX_VISIBLE_TAGS}
        onClick={onTagClick}
      />
    </span>
  );
}

interface VersionsSubTableProps {
  summary: SkillSummary;
  /** True when the caller authored the skill. Author-only: deleting the last
   * remaining version (which cascades into a whole-skill delete). */
  isAuthor: boolean;
  /** True when the caller may edit version content — the author, or a
   * subscriber of an org-editable skill. Drives Edit/Delete vs. Inspect. */
  canEdit: boolean;
  /** True when the caller has a subscription row (always true for authors). */
  subscribed: boolean;
  /** Currently pinned version for the caller's AI, or null if not exposed to AI. */
  aiPinnedVersion: number | null;
  /** True while a setSubscription mutation is in flight for this skill. */
  aiVersionPending: boolean;
  onSetAiVersion: (version: number | null) => void;
  onRequestDeleteVersion: (version: number, isLast: boolean) => void;
}

/**
 * Per-version table rendered inside an expanded skill row. Each row shows:
 *
 * - the version number,
 * - the "when to use" text,
 * - an **Available to AI** Switch wired to the caller's subscription
 *   ``ai_version`` (at most one version per skill can be on — flipping a
 *   second version takes the pin from the previously-on row),
 * - a copy-link and a View action, both pinned to that exact version, plus
 *   Edit / Delete for the author or an org-editable subscriber.
 */
function VersionsSubTable({
  summary,
  isAuthor,
  canEdit,
  subscribed,
  aiPinnedVersion,
  aiVersionPending,
  onSetAiVersion,
  onRequestDeleteVersion,
}: VersionsSubTableProps) {
  const versionsQuery = useSkillVersions(summary.skill.skill_id);

  const versions = versionsQuery.data ?? [];

  if (versionsQuery.isLoading) {
    return <div className={styles.versionEmpty}>Loading versions…</div>;
  }
  if (versions.length === 0) {
    return <div className={styles.versionEmpty}>No versions yet.</div>;
  }

  return (
    <Table size="small" className={styles.versionsTable}>
      <TableHead>
        <TableRow>
          <TableCell width={80}>Version</TableCell>
          <TableCell>When to use</TableCell>
          {subscribed && (
            <TableCell width={120} align="center">
              Available to AI
            </TableCell>
          )}
          <TableCell width={170} align="right">
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {versions.map((version) => {
          const isLastVersion = versions.length === 1;
          const isPinned = aiPinnedVersion === version.version;
          let aiTooltip: string;
          if (isPinned) {
            aiTooltip = `AI invokes this version. Click to disable.`;
          } else if (aiPinnedVersion !== null) {
            aiTooltip = `Click to switch the AI pin from v${aiPinnedVersion} to v${version.version}.`;
          } else {
            aiTooltip = `Click to pin v${version.version} for AI auto-invocation.`;
          }
          // A non-author editor may delete versions, but not the last one —
          // that cascades into a whole-skill delete, which is author-only.
          const lastVersionDeleteBlocked = !isAuthor && isLastVersion;
          let deleteTooltip: string;
          if (lastVersionDeleteBlocked) {
            deleteTooltip =
              "Only the author can delete the skill's last remaining version.";
          } else if (isLastVersion) {
            deleteTooltip =
              "Deleting the only version will also delete the skill";
          } else {
            deleteTooltip = `Delete v${version.version}`;
          }
          return (
            <TableRow key={version.version}>
              <TableCell>v{version.version}</TableCell>
              <TableCell>
                <div
                  className={styles.versionDescription}
                  title={version.description}
                >
                  {version.description}
                </div>
              </TableCell>
              {subscribed && (
                <TableCell align="center">
                  <RobotoTooltip title={aiTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        color={isPinned ? "primary" : "default"}
                        onClick={() =>
                          onSetAiVersion(isPinned ? null : version.version)
                        }
                        disabled={aiVersionPending}
                        aria-label={`Available to AI for ${summary.skill.name} v${version.version}`}
                        aria-pressed={isPinned}
                      >
                        {isPinned ? (
                          <PushPinIcon fontSize="small" />
                        ) : (
                          <PushPinOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                </TableCell>
              )}
              <TableCell align="right">
                <div className={styles.actions}>
                  {/* Pins the version: whoever opens the link lands on this
                      exact body, not on whatever is latest by then. */}
                  <CopyLinkButton
                    url={buildSkillShareLink({
                      skillId: summary.skill.skill_id,
                      version: version.version,
                    })}
                    tooltip={`Copy a link to v${version.version}`}
                  />
                  {/* Opens this version's page, where the body renders as
                      Markdown. Authors get it too: the row's pen edits the raw
                      text, and this is how they preview the result. */}
                  <RobotoTooltip title={`View v${version.version}`}>
                    <span>
                      <IconButton
                        size="small"
                        component={Link}
                        to={Goto.createSkillRoute(
                          summary.skill.skill_id,
                          version.version,
                        )}
                        aria-label={`View v${version.version}`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                  {canEdit && (
                    <>
                      {/* Same destination as View, opened in edit mode — one
                          surface for reading and writing a version, rather than
                          a page for one and a dialog for the other. */}
                      <RobotoTooltip title={`Edit v${version.version}`}>
                        <span>
                          <IconButton
                            size="small"
                            component={Link}
                            to={skillEditPath(
                              summary.skill.skill_id,
                              version.version,
                            )}
                            aria-label={`Edit v${version.version}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                      <RobotoTooltip title={deleteTooltip}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onRequestDeleteVersion(
                                version.version,
                                isLastVersion,
                              )
                            }
                            disabled={lastVersionDeleteBlocked}
                            aria-label={`Delete v${version.version}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface DeleteVersionDialogProps {
  target: VersionDeleteTarget | null;
  orgId: string | undefined;
  onClose: () => void;
}

function DeleteVersionDialog({
  target,
  orgId,
  onClose,
}: DeleteVersionDialogProps) {
  const deleteVersion = useDeleteVersion(orgId);

  async function performDelete(): Promise<void> {
    if (target === null) {
      return;
    }
    try {
      await deleteVersion.mutateAsync({
        skillId: target.summary.skill.skill_id,
        version: target.version,
      });
    } finally {
      onClose();
    }
  }

  const title = target
    ? `Delete version v${target.version} of "${target.summary.skill.name}"?`
    : "Delete version";

  return (
    <AlertDialog
      dialogOpen={target !== null}
      handleClose={() => {
        if (!deleteVersion.isPending) {
          onClose();
        }
      }}
      dialogTitle={title}
      dialogContent={
        <div className={styles.deleteWarning}>
          <span className={styles.deleteWarningEmphasis}>
            This is permanent.
          </span>
          <span>
            {target?.isLastVersion
              ? "This is the only remaining version. Deleting it will also remove the skill itself."
              : "Existing chats keep any skill_invocation messages they've already produced — the body is captured at invocation time."}
          </span>
        </div>
      }
      dialogAction={performDelete}
    />
  );
}
